/**
 * The student profile, and the language preference that rides on it (§30).
 *
 * Every screen needs the profile — the greeting, the curriculum class, the
 * language. It lives in one query so there is a single fetch and a single
 * cache entry (§32), and adopting the saved language happens here rather than
 * in each screen that needs it.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { studentApi } from '@/api/endpoints';
import { isApiError } from '@/api/errors';
import { useAuth } from '@/features/auth/AuthProvider';
import { useLanguage } from '@/features/language/LanguageProvider';
import { queryKeys } from '@/app/query';

export function useProfile() {
  const { status } = useAuth();
  const { adoptPreference } = useLanguage();

  const query = useQuery({
    queryKey: queryKeys.profile,
    queryFn: studentApi.profile,
    enabled: status === 'authenticated',
    retry(failureCount, error) {
      // A missing profile is the first-run state, not a failure: §48 says the
      // child provides class and board after the magic link, so 404 here is
      // expected and must not be retried.
      if (isApiError(error) && error.code === 'RESOURCE_NOT_FOUND') return false;
      return failureCount < 2;
    },
  });

  const preferred = query.data?.preferred_language;
  useEffect(() => {
    if (preferred) adoptPreference(preferred);
  }, [preferred, adoptPreference]);

  const missingProfile =
    query.isError && isApiError(query.error) && query.error.code === 'RESOURCE_NOT_FOUND';

  return { ...query, missingProfile };
}
